// @givmo/app-admin — the donor disclosure for a company's direct gift (CAM-X4 web). ★ MONEY-MOVING ★
//
// The company is the donor of a direct gift, so before it pays it is shown the registry body for a
// COMPANY payer on the chosen RAIL and TARGET (CAM-X3, D-CAM-26 ruling E): a company-card gift is a
// contribution with a receipt; a company-wallet gift is a grant from funds Givmo Charitable Fund
// already holds, with no new receipt. The server derives the consent posture (A_UNCLAIMED vs
// B_VERIFIED) per target, so every (target, rail) pair is its own resolve.
//
// The surface keys are the backend gate's own mapping (`donation_service.py`), the same the donate
// app and iOS use:
//
//   charity    → DONATE_CONFIRMATION (charity_id)
//   fundraiser → DONATE_CONFIRMATION (fundraiser_id)
//   etf        → CAUSE_ETF          (etf_id)
//
// The resolved document's id + hash ride the donation body as the `shown_disclaimer_*` echo; the
// gate checks them against what it expects for this gift before any charge (`409 DISCLOSURE_STALE`
// on a mismatch, nothing charged).

import { givmoClient } from '@givmo/api-client';
import {
  createDisclosureCache,
  resolveDisclosure,
  type DisclosureFunding,
  type ResolvedDisclosure,
} from '@givmo/disclosure';
import type { Funding, GiveTarget, TargetType } from './give-decode';

/** The registry surface per direct-giving target type (the backend gate's own mapping). */
export const GIVE_DISCLOSURE_SURFACE: Record<TargetType, string> = {
  charity: 'DONATE_CONFIRMATION',
  fundraiser: 'DONATE_CONFIRMATION',
  etf: 'CAUSE_ETF',
};

/** The resolve's rail for a funding source: a company card is `card`, the company wallet `wallet`. */
export function giveDisclosureRail(funding: Funding): DisclosureFunding {
  return funding.kind === 'wallet' ? 'wallet' : 'card';
}

// One ETag cache for the session: reopening the same gift revalidates with a conditional GET
// (304 = headers only) instead of refetching the body.
const cache = createDisclosureCache();

/**
 * Resolve the company-payer body for a gift. `null` = nothing to show — a 404, a server error or a
 * transport failure alike (the resolver never throws); the dialog treats that as blocking.
 */
export function resolveGiveDisclosure(
  target: Pick<GiveTarget, 'type' | 'id'>,
  rail: DisclosureFunding,
): Promise<ResolvedDisclosure | null> {
  return resolveDisclosure({
    client: givmoClient,
    surface: GIVE_DISCLOSURE_SURFACE[target.type],
    target: { kind: target.type, id: target.id },
    funding: rail,
    payer: 'company',
    cache,
  });
}
